import { useEffect, useRef, useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import ModalRaw from 'components/Modals/presets/BasicModal';
import MainSelectorRaw from 'components/Kizen/MainSelector';
import Loader from '__components/Kizen/Loader';
import { Spacer } from '@kizen/kds/Spacer';
import { layers } from 'app/spacing';
import { getAIUsageCyclesQuery, getAIUsageSummaryQuery } from 'query/billing';
import { toastVariant, useToast } from '__components/ToastProvider';
import { UsageCycleSelect } from './UsageCycleSelect';
import { UsageByAreaTable } from './UsageByAreaTable';
import { UsageBreakdownTable } from './UsageBreakdownTable';
import { UsageBreakdownSearch } from './UsageBreakdownSearch';
import { useUsageBreakdownData } from './useUsageBreakdownData';
import {
  HeaderRow,
  InitialLoadOverlay,
  ModalContent,
  SearchToggle,
  TabPanel,
} from './styles';

const Modal = ModalRaw as ComponentType<any>;
const MainSelector = MainSelectorRaw as ComponentType<any>;

const SECTIONS = {
  workflows: 'workflows',
  users: 'users',
} as const;

const sectionSelectStyles = {
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    zIndex: layers.popoverInModal,
    marginTop: -2,
  }),
};

type Props = {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void | Promise<void>;
};

export const AiUsageModal = (props: Props) => {
  const [usageCycle, setUsageCycle] = useState<string | undefined>(undefined);
  const [activeSection, setActiveSection] = useState<string>(
    SECTIONS.workflows
  );
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const { t } = useTranslation();
  const [showToast] = useToast();

  const { data: cyclesData, isPending: cyclesPending } = useQuery(
    getAIUsageCyclesQuery({ enabled: props.show, throwOnError: false })
  );
  const {
    data: summaryData,
    isLoading: summaryLoading,
    isPending: summaryPending,
    isError: summaryIsError,
  } = useQuery(
    getAIUsageSummaryQuery({
      params: { usage_cycle: usageCycle },
      placeholderData: keepPreviousData,
      enabled: props.show,
      throwOnError: false,
    })
  );

  // Guards against showing the toast again on every re-render
  const shownSummaryErrorRef = useRef(false);
  if (summaryIsError && !shownSummaryErrorRef.current) {
    shownSummaryErrorRef.current = true;
    showToast({
      message: t('Failed to load usage data for this cycle.'),
      variant: toastVariant.FAILURE,
    });
  } else if (!summaryIsError) {
    shownSummaryErrorRef.current = false;
  }

  const workflowsBreakdown = useUsageBreakdownData({
    bucket: 'agentic-workflows',
    usageCycle,
    enabled: props.show && activeSection === SECTIONS.workflows,
  });
  const usersBreakdown = useUsageBreakdownData({
    bucket: 'team-members',
    usageCycle,
    enabled: props.show && activeSection === SECTIONS.users,
  });

  const activeBreakdownPending =
    activeSection === SECTIONS.workflows
      ? workflowsBreakdown.isPending
      : usersBreakdown.isPending;
  useEffect(() => {
    if (
      !initialLoadDone &&
      !cyclesPending &&
      !summaryPending &&
      !activeBreakdownPending
    ) {
      setInitialLoadDone(true);
    }
  }, [initialLoadDone, cyclesPending, summaryPending, activeBreakdownPending]);

  const sectionOptions = [
    { value: SECTIONS.workflows, label: t('AI Usage by Agentic Workflows') },
    { value: SECTIONS.users, label: t('AI Chat Usage by Users') },
  ];

  return (
    <Modal
      {...props}
      heading={t('AI Usage')}
      size="large"
      fitContent={false}
      buttonText={t('Close')}
      actionBtnColor="blue"
      leftBtn={null}
      typeOfContent="no-paddings"
      displayFlex
      data-qa-id="ai-usage-modal"
    >
      <ModalContent className="kds-expanding-scrollbar">
        {!initialLoadDone ? (
          <InitialLoadOverlay>
            <Loader loading paddingTop={0} />
          </InitialLoadOverlay>
        ) : null}
        <UsageCycleSelect
          cycles={cyclesData}
          value={usageCycle}
          onChange={setUsageCycle}
        />
        <Spacer size={20} />
        <UsageByAreaTable
          data={summaryData}
          loading={summaryLoading}
          isError={summaryIsError}
        />
        <Spacer size={20} />
        <HeaderRow>
          <MainSelector
            options={sectionOptions}
            value={sectionOptions.find(
              (option) => option.value === activeSection
            )}
            onChange={(option: (typeof sectionOptions)[number]) =>
              setActiveSection(option.value)
            }
            maxWidth={500}
            styles={sectionSelectStyles}
            data-qa-id="ai-usage-section-select"
          />
          <SearchToggle active={activeSection === SECTIONS.workflows}>
            <UsageBreakdownSearch
              bucket="agentic-workflows"
              value={workflowsBreakdown.pagination.actualSearch}
              onChange={workflowsBreakdown.pagination.handleChangeSearch}
              loading={workflowsBreakdown.isLoading}
            />
          </SearchToggle>
          <SearchToggle active={activeSection === SECTIONS.users}>
            <UsageBreakdownSearch
              bucket="team-members"
              value={usersBreakdown.pagination.actualSearch}
              onChange={usersBreakdown.pagination.handleChangeSearch}
              loading={usersBreakdown.isLoading}
            />
          </SearchToggle>
        </HeaderRow>
        <Spacer size={5} />
        <TabPanel active={activeSection === SECTIONS.workflows}>
          <UsageBreakdownTable
            bucket="agentic-workflows"
            data={workflowsBreakdown.data}
            isLoading={workflowsBreakdown.isLoading}
            isError={workflowsBreakdown.isError}
            pagination={workflowsBreakdown.pagination}
          />
        </TabPanel>
        <TabPanel active={activeSection === SECTIONS.users}>
          <UsageBreakdownTable
            bucket="team-members"
            data={usersBreakdown.data}
            isLoading={usersBreakdown.isLoading}
            isError={usersBreakdown.isError}
            pagination={usersBreakdown.pagination}
          />
        </TabPanel>
      </ModalContent>
    </Modal>
  );
};
